import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fade,
  Stack,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { io } from "socket.io-client";
import Header from "../header/Header";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SendIcon from "@mui/icons-material/Send";

export default function QrCodePage() {
  const navigate = useNavigate();
  const { businessName, carrierCanonicalId, canonicalId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

  useEffect(() => {
    if (typeof window !== "undefined") {
      console.log("Detected device:", isMobile ? "mobile" : "pc");
      localStorage.setItem("flow-source", isMobile ? "mobile" : "pc");
    }
  }, [isMobile]);
  useEffect(() => {
    localStorage.removeItem("sessionId");
    localStorage.removeItem("flowStartedOn");
  }, []);
  const [socket, setSocket] = useState<any>(null);
  const [showContent, setShowContent] = useState(false);
  const thisUrl = process.env.REACT_APP_API_URL || "";
  function generateSessionId(length: number = 24): string {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let sessionId = "";
    for (let i = 0; i < length; i++) {
      sessionId += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return sessionId;
  }

  const uploadUrl = `${
    window.location.origin
  }/${businessName}/${carrierCanonicalId}/upload-items/${canonicalId}/${generateSessionId()}`;
  console.log(uploadUrl);

  // WebSocket setup (for PC)
  useEffect(() => {
    // if (isMobile) return;

    const newSocket = io(thisUrl);
    setSocket(newSocket);

    newSocket.on("connect", () => console.log("Connected to websocket"));

    newSocket.on("upload_complete", () => {
      console.log("SOCKET RECEIVED");
      navigate(
        `/${businessName}/${carrierCanonicalId}/inventory/${canonicalId}`
      );
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ backgroundColor: "#f2f0f0", minHeight: "100vh" }}>
      <Header />

      <Container
        sx={{
          width: "100%",
          maxWidth: "1100px",
          py: isMobile ? 4 : 8,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "center",
          gap: isMobile ? 4 : 8,
          textAlign: isMobile ? "center" : "left",
        }}
      >
        {/* ✅ Left Section (QR Code) */}
        <Fade in={showContent} timeout={600}>
          <Paper
            elevation={4}
            sx={{
              p: 4,
              borderRadius: "16px",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              width: isMobile ? "100%" : "45%",
            }}
          >
            <Typography
              fontWeight={700}
              fontSize={isMobile ? "18px" : "22px"}
              mb={1.5}
            >
              Scan QR Code to Continue Uploading
            </Typography>

            <Typography
              fontSize={isMobile ? "14px" : "16px"}
              color="text.secondary"
              mb={3}
              maxWidth="300px"
            >
              Open this link on your mobile device to upload images.
            </Typography>

            <QRCode value={uploadUrl} size={isMobile ? 180 : 240} />

            <Typography
              mt={3}
              fontSize="14px"
              color="text.secondary"
              maxWidth="300px"
            >
              Once images are uploaded on mobile, you’ll be redirected to your
              inventory automatically.
            </Typography>
          </Paper>
        </Fade>

        {/* 🧾 Right Section (Instructions) */}
        <Fade in={showContent} timeout={800}>
          <Paper
            elevation={3}
            sx={{
              p: isMobile ? 3 : 5,
              borderRadius: "16px",
              backgroundColor: "#fff",
              width: isMobile ? "100%" : "45%",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={700}
              fontSize={isMobile ? "18px" : "20px"}
              mb={2}
              color="text.primary"
            >
              Quick Steps to Complete Upload
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="1. Scan the QR code on your mobile device"
                  secondary="This opens the upload page instantly."
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CameraAltIcon color="secondary" />
                </ListItemIcon>
                <ListItemText
                  primary="2. Capture and upload room photos"
                  secondary="Use your phone camera for quick photo capture."
                />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <SendIcon sx={{ color: "#4CAF50" }} />
                </ListItemIcon>
                <ListItemText
                  primary="3. Submit and wait for success confirmation"
                  secondary="Once done, you'll see a success message."
                />
              </ListItem>
            </List>

            <Divider sx={{ my: 3 }} />

            <Typography
              fontSize={isMobile ? "13px" : "14px"}
              color="text.secondary"
              mb={2}
            >
              Or continue uploading directly from here:
            </Typography>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Button
                variant="contained"
                onClick={() =>
                  navigate(
                    `/${businessName}/${carrierCanonicalId}/upload-items/${canonicalId}`
                  )
                }
                sx={{
                  borderRadius: "20px",
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  px: isMobile ? 3 : 5,
                  py: 1,
                  textTransform: "none",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: "600",
                  mb: 2,
                  "&:hover": { backgroundColor: "#357ABD" },
                }}
              >
                Continue on this device
              </Button>

              {/* <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  borderRadius: "20px",
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  px: isMobile ? 3 : 5,
                  py: 1,
                  textTransform: "none",
                  fontSize: isMobile ? "13px" : "14px",
                  fontWeight: "600",
                }}
              >
                Go Back
              </Button> */}
            </Stack>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
}
